/*eslint-disable */
import {
  DivIcon,
  GeoJSON, LatLng, LayerGroup, LeafletEvent, Marker, markerClusterGroup, marker,
} from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import IFeature from '@/interfaces/IFeature';
import { center } from '@turf/turf';
import IPosition from '@/interfaces/IPosition';
import { IFeatureCollection } from '@/interfaces/IFeatureCollection';
import { IParkingData } from '@/interfaces/IParkingData';
import { toRaw } from 'vue';

export default abstract class LayerService {
  protected clusteringConstant = 60;
  protected clusterWidth = 65;
  protected clusterHeight = 65;
  protected markerWidth = 47;
  protected markerHeight = 57;

  abstract redrawLayer(): LayerGroup;

  public noData: string = 'b/d';

  // eslint-disable-next-line global-require
  protected markerIcon = 'obszary-parkingowe.svg';

  /**
   * Handles a click on a map object
   * @param e {LeafletEvent} event object
   * @param metaData {any} object that has metadata
   * @param leafletMarker {Marker} leaflet marker for the object itself or it's center
   */
  abstract onClick(e: LeafletEvent, metaData: any, leafletMarker: Marker): void;

  /**
   * Generates a {LayerGroup} filled with markers element from a GeoJSON position collection
   * @param positions {IFeatureCollection} list of GeoJSON positions to be drawn on the map
   */
  protected generateMarkerClusterLayer = (positions: IFeatureCollection): LayerGroup => {
    const markerClusterGroupInstance = markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 40,
    });

    positions.features.forEach((feature: any) => {
      if (!feature.geometry) return;

      const { coordinates } = feature.geometry;
      const [lng, lat] = coordinates as Array<number>;

      const singleMarker = this.createMarker({ lat, lng }, feature);
      markerClusterGroupInstance.addLayer(singleMarker);
    });

    return markerClusterGroupInstance;
  };


  public createMarker = (position: IPosition, feature: IFeature): Marker => {
    const singleMarker = marker([position.lat, position.lng], {
      icon: new DivIcon({
        html: this.generateHtmlPointTemplate(feature.properties),
        iconSize: [this.markerWidth, this.markerHeight],
        iconAnchor: [this.markerWidth / 2, this.markerHeight],
      }),
      draggable: false,
    });

    singleMarker.on('click', (e) => {
      this.onClick(e, feature.properties, singleMarker);
    });

    return singleMarker;
  };

  /**
   * Generates a {LayerGroup} filled with polygons from elements from a GeoJSON position collection.
   * @param polygons {IFeatureCollection} GeoJSON FeatureCollection
   */
  protected generatePolygonsLayer = (polygons: IFeatureCollection, isBorder: boolean = false): LayerGroup => {
    const layerGroup = new LayerGroup();

    const polygonsFeatures = polygons.features.filter((feature: IFeature) => feature.hasOwnProperty('geometry'));

    polygonsFeatures.map((feature: IFeature): GeoJSON => {
        const [lat, lng] = this.findPolygonCenter(feature);
        const metaData = feature.properties as any;
        // any used because IFeature definition isn't exactly the same as leaflets
        // internal GeoJSON feature definition
        const polygon: GeoJSON = new GeoJSON(feature as any, {
          coordsToLatLng: this.coordsToLatLng,
        });

        // Set polygon color
        polygon.setStyle({
          color: this.setColor(feature),
          weight: isBorder ? 6 : 4,
          className: isBorder ? '--stroke-border' : '',
        });

        polygon.on('click', (e) => {
          this.onClick(e, metaData, new Marker(new LatLng(lat, lng)));
        });

        layerGroup.addLayer(toRaw(polygon));

        return polygon;
    });
    return layerGroup;
  };

  /**
   * Generates a {LayerGroup} polylines from elements from a GeoJSON position collection.
   * @param polylines {IFeatureCollection} GeoJSON FeatureCollection
   */
  protected generatePolyLinesLayer = (polylines: IFeatureCollection): LayerGroup => {
    const layerGroup = new LayerGroup();

    polylines.features.map((feature): GeoJSON => {
      const [lat, lng] = this.findPolygonCenter(feature);
      const metaData = feature.properties as any;
      // any used because IFeature definition isn't exactly the same as leaflets
      // internal GeoJSON feature definition
      const polyline: GeoJSON = new GeoJSON(feature as any, {
        coordsToLatLng: this.coordsToLatLng,
      });

      // Set polygon color
      polyline.setStyle({
        color: feature.properties.data.color,
        weight: 4,
        className: 'polyline-path '+'polyline-id',
      });

      polyline.on('click', (e) => {
        this.onClick(e, metaData, new Marker(new LatLng(lat, lng)));
      });

      layerGroup.addLayer(toRaw(polyline));
      return polyline;
    });

    return layerGroup;
  };
  /**
   * Generates a {LayerGroup} polylines from elements from a GeoJSON position collection.
   * @param polylines {IFeatureCollection} GeoJSON FeatureCollection
   */
  protected generateParkingPolyLinesLayer = (polylines: IFeatureCollection): LayerGroup => {
    const layerGroup = new LayerGroup();
    if (polylines.features.length) {
      polylines.features.map((feature): GeoJSON => {
        const [lat, lng] = this.findPolygonCenter(feature);
        const metaData = feature.properties as any;
        // any used because IFeature definition isn't exactly the same as leaflets
        // internal GeoJSON feature definition
        const polyline: GeoJSON = new GeoJSON(feature as any, {
          coordsToLatLng: this.coordsToLatLng,
        });
  
        // Set polygon color
        polyline.setStyle({
          color: this.setColor(feature),
          weight: 6,
          className: '--stroke-border',
        });
  
        polyline.on('click', (e) => {
          this.onClick(e, metaData, new Marker(new LatLng(lat, lng)));
        });
  
        layerGroup.addLayer(toRaw(polyline));
        return polyline;
      });
    }


    return layerGroup;
  };

  /**
   * Set Color for different type and spots
   *
   * @param feature
   */
  protected setColor(feature: IFeature): string {
    const color = feature.properties.color;
    if (!!color) {
      return color;
    }

    if (feature.geometry.type === 'MultiLineString' && feature.properties.last_status) {
      let color = '';

      const occupiedPercent = +((100 * feature.properties.last_status.occupiedSpots) / parseInt(feature.properties.parkingSpots)).toFixed(0);
      if (occupiedPercent <= 50) {
        color = '#28972f';
      } else if (occupiedPercent > 50 && occupiedPercent <= 80) {
        color = '#FFE11B';
      } else if (occupiedPercent > 80) {
        color = '#de3e3e';
      } else {
        color = '#de3e3e';
      }

      return color;
    }

    return '#de3e3e';
  }

  /**
   * Generates the content for a {DivIcon} that will be put on map.
   * @param metaData {any} Metadata about the marker object (content of {IFeature} properties)
   */
  protected generateHtmlPointTemplate = (metaData: any) => {
    const templateData: IParkingData = metaData as IParkingData;
    const template = `
        <div class="map-custom-icon"
        style='background-image: url("${require('@/assets/img/map-icons/' + this.markerIcon)}")'>
        <div>
      `;

    return template;
  };

  /**
   * Returns an [lat, lng] array with the center of GeoJSON object.
   * @param polygonData GeoJSON object that is which center is going to be found.
   */
  protected findPolygonCenter =
    (polygonData: IFeature): number[] => {
      const polygon: GeoJSON = new GeoJSON(polygonData as any, {
        coordsToLatLng: this.coordsToLatLng,
      });
      const centroidPoint: any = center(polygon.toGeoJSON() as any);
      return [
        centroidPoint.geometry.coordinates[1],
        centroidPoint.geometry.coordinates[0],
      ];
    };

  /**
   * LatLng conversion method for use with Leaflet GeoJson constructor
   * @param coords input coordinates
   */
  protected coordsToLatLng = (coords: [number, number] | [number, number, number]): LatLng => {
    if (coords.length > 3) {
      return new LatLng(coords[1], coords[0]);
    }

    return new LatLng(coords[1], coords[0], coords[2]);
  };

  /**
   * Merge object with the same property
   * @param array
   * @param property
   */
  protected mergeObjectsInUnique = (items: any[], property: string): [] => {
    const newArray = new Map();

    items.forEach((item: any) => {
      const propertyValue = item[property];
      if (newArray.has(propertyValue)) {
        newArray.set(propertyValue, { ...item, ...newArray.get(propertyValue) });
      } else {
        newArray.set(propertyValue, item);
      }
    });

    return [...newArray.values()] as [];
  }

  /**
   * Sum values in array by key name
   * @param items
   * @param rowCountName
   */
  sumParkingCategoryPlaces = (items: [], rowCountName: any):number => {
    const sum = items.map(item => item[rowCountName])
      .reduce((prev, curr) => prev + curr, 0);
    return sum;
  };
}
